import { useMantineTheme } from "@mantine/core";
import { rem } from "@mantine/core";
import { FileRejection, FileWithPath, Dropzone as MantineDropzone } from "@mantine/dropzone";
import { IconUpload, IconX } from "@tabler/icons-react";
import { t } from "i18next";

import { showErrorToast } from "#app/notifications";
import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { Icon } from "#shared/icons/Icon";
import { Illustration } from "#shared/icons/Illustration";
import { Group } from "#shared/view/components/Group";
import { PartFileButton } from "#shared/view/components/PartFileButton";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";

type Props = {
  onDrop: (files: FileWithPath[]) => void;
  title: string;
  subTitle: string;
  accept?: { [key: string]: string[] } | string[];
  validator?: (files: File) => void;
  loading?: boolean;
  maxSize?: number;
};

const MAX_SIZE = 500 * 1024 ** 2;

export const Dropzone = ({
  onDrop,
  subTitle,
  title,
  accept,
  validator,
  loading,
  maxSize = MAX_SIZE,
}: Props) => {
  const theme = useMantineTheme();

  return (
    <MantineDropzone
      onDrop={onDrop}
      rejectColor={colors.red[6]}
      onReject={(rejectedFiles) => {
        const errorCode = getErrorCode(rejectedFiles);
        const errorMessage = getErrorMessage(errorCode);
        showErrorToast({ title: errorMessage });
      }}
      maxSize={maxSize}
      accept={accept}
      // FileError Type is not export by @mantine/dropzone
      // oxlint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      validator={validator}
      multiple={false}
      loading={loading}
      style={{
        backgroundColor: colors.blue[0],
        borderColor: colors.blue[4],
      }}
    >
      <Stack
        align="center"
        gap="xxs"
        style={{
          minHeight: rem(100),
          pointerEvents: "none",
        }}
      >
        <MantineDropzone.Accept>
          <IconUpload size="3.2rem" stroke={1.5} color={colors[theme.primaryColor]?.[6]} />
        </MantineDropzone.Accept>
        <MantineDropzone.Reject>
          <IconX size="3.2rem" stroke={1.5} color={colors.red[6]} />
        </MantineDropzone.Reject>
        <MantineDropzone.Idle>
          <Illustration name="folder" />
        </MantineDropzone.Idle>

        <Text size="xl" inline fw={500}>
          {title}
        </Text>
        {!!subTitle && (
          <Text size="sm" c="dimmed" inline mt={7}>
            {subTitle}
          </Text>
        )}

        <Group justify="center" align="center">
          <div style={{ width: 35, height: 1, backgroundColor: colors.blue[5] }} />
          <Text size="sm" c={colors.blue[5]} fz={fontSizes.medium}>
            {"OU"}
          </Text>
          <div style={{ width: 35, height: 1, backgroundColor: colors.blue[5] }} />
        </Group>
        <PartFileButton
          buttonLabel={t("similarPart.dropzone.uploadFile")}
          buttonIcon={<Icon name="import" />}
          buttonVariant="filled"
          onChange={(files) => onDrop(files.map((file) => file.data))}
        />
      </Stack>
    </MantineDropzone>
  );
};

type ErrorCodes = "file-too-large" | "file-invalid-type";

const getErrorCode = (rejectedFiles: FileRejection[]) => {
  if (rejectedFiles.length > 0) {
    return rejectedFiles[0]?.errors[0]?.code as ErrorCodes | undefined;
  }

  return;
};

const getErrorMessage = (code: ErrorCodes | undefined) => {
  const errorCodes = {
    "file-too-large": "Le fichier est trop volumineux.",
    "file-invalid-type": "Le type de fichier n'est pas valide. Fichiers .stp ou .step uniquement",
  };

  if (!code) {
    return "Une erreur est survenue.";
  }

  return errorCodes[code];
};
